import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';

import { HydraState } from '@src/constants';
import { useScheduleQuestionsStore } from '@src/store';

export const QUERY_KEY = 'getQuestions';

const getFormSectionQuestions = async ({
	form,
	locale,
	sessionId = 'N/A',
	signal,
}: {
	form: string;
	locale?: string | string[];
	sessionId: string | undefined;
	signal: AbortSignal | undefined;
}) => {
	const queryParams = `?form=${form}&language=${locale}`;
	try {
		const response = await fetch(process.env.APP_PATH + '/api/clear-choice/get-patient-questions/' + queryParams, {
			headers: {
				'X-Session-Id': sessionId,
				hydra: HydraState.ON,
			},
			method: 'GET',
			signal,
		});

		if (!response.ok) {
			const errorData = await response.json().catch(() => ({}));
			throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
		}

		return await response.json();
	} catch (error) {
		console.error(`Failed Loading Form Questions:`, error);
		throw error;
	}
};

export function useFormQuestions({
	form,
	locale,
	sessionId = 'N/A',
}: {
	form: string;
	locale?: string | string[];
	sessionId?: string;
}) {
	const { setQuestionData, setQuestions } = useScheduleQuestionsStore((store) => store);
	const { data, isLoading, error } = useQuery({
		queryKey: [QUERY_KEY, form, locale, sessionId],
		queryFn: async ({ signal }) => getFormSectionQuestions({ form, locale, sessionId, signal }),
	});

	useEffect(() => {
		if (!isLoading) {
			setQuestionData(data?.form?.sections?.[0]?.questions);
			setQuestions(data?.form?.sections?.[0]?.questions);
		}
	}, [data?.form?.sections, isLoading, setQuestionData, setQuestions]);

	return {
		data,
		isLoading,
		error,
	};
}
